// ============================================================================
//  VenueLivePage — /event/:slug/live — the cinematic projector view.
//
//  Per the user's brief (May 2026):
//    "Apple keynote meets nightclub visual wall."
//    "Once spins start appearing on the screens, the booth stops being
//     a side activity and becomes part of the event atmosphere itself."
//
//  Design rules locked:
//    • NO browser chrome feel — no nav, no buttons, no admin UI
//    • SAME pacing/typography/overlays/glow as Spin Film 360 playback
//    • Audio-reactive pulses on every SSE tick (share / playback / spin)
//    • 6 scenes rotate every ~10s — Spread Score hero, the 4 best-spin
//      categories (Trending/Crowd Favourite/Most Replayed/Biggest
//      Reaction), and a logo interstitial
//    • Renders ONLY scenes with data — empty events show just the
//      Spread Score + Logo loop (no dead frames)
//
//  EventDrop Originals: matches Booth360Page reveal language —
//  letter-spacing, gold accent, slow framer-motion easings, glass-
//  morphism only when content is meant to feel "premium-static".
// ============================================================================
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import { useMemorySnapArrivals } from '../hooks/useMemorySnapArrivals';
import MemoryHeroMoment from '../components/MemoryHeroMoment';
import { SceneDropOneWord, SceneMemoryWallTicker, SceneQRPlate } from './venue-live-scenes';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Iter 170: slowed from 10s → 14s per the user's "let the scenes breathe"
// brief. Soft fades only; no hard cuts, no zoom explosions, no particles.
const SCENE_MS = 14000;

// Rate-limit for cinematic audio. User brief: "1 featured audio moment
// every 20-40s. You want sparse, impactful, cinematic — not noisy,
// arcade-like." 25s lands inside that window with the silence
// in-between letting each sting LAND emotionally.
const VENUE_AUDIO_MIN_INTERVAL_SEC = 25;
const SCENE_THAT_PLAYS_AUDIO = new Set(['best']);   // ONLY best-spin scenes (NOT score / logo)
const STATUS_THEME = {
  quiet:           { label: 'Quiet',          ring: '#7F8593', glow: 'rgba(127,133,147,0.45)' },
  growing:         { label: 'Growing',        ring: '#3FA7FF', glow: 'rgba(63,167,255,0.55)' },
  spreading_fast:  { label: 'Spreading Fast', ring: '#B89762', glow: 'rgba(184,151,98,0.65)' },
  viral_tonight:   { label: 'Viral Tonight',  ring: '#D4AF5E', glow: 'rgba(212,175,94,0.75)' },
};
const CATEGORY_ACCENT = {
  trending:         { color: '#FF6B6B', glow: 'rgba(255,107,107,0.6)' },
  crowd_favourite:  { color: '#D4AF5E', glow: 'rgba(212,175,94,0.6)'  },
  most_replayed:    { color: '#B89762', glow: 'rgba(184,151,98,0.6)'  },
  biggest_reaction: { color: '#FFB84D', glow: 'rgba(255,184,77,0.6)'  },
};

// ────────────────────────────────────────────────────────────────────
//  Scenes — small focused renderers, each takes the same `live` prop.
// ────────────────────────────────────────────────────────────────────

const SceneSpreadScore = ({ live, pulseKey }) => {
  const theme = STATUS_THEME[live.score?.status_slug] || STATUS_THEME.quiet;
  const score = Math.max(0, Math.min(100, Number(live.score?.score) || 0));
  const C = 2 * Math.PI * 200; // r=200
  const dashOffset = C * (1 - score / 100);
  return (
    <div style={sceneStage} data-testid="venue-scene-spread-score">
      {/* Radial halo — pulses on each SSE tick to give the ROOM a beat */}
      <motion.div
        key={`pulse-${pulseKey}`}
        aria-hidden
        initial={{ opacity: 0.35, scale: 0.96 }}
        animate={{ opacity: [0.35, 0.8, 0.4], scale: [0.96, 1.04, 1] }}
        transition={{ duration: 1.6, ease: 'easeOut' }}
        style={{
          position: 'absolute', inset: 0,
          background: `radial-gradient(circle at 50% 45%, ${theme.glow} 0%, transparent 55%)`,
          pointerEvents: 'none', filter: 'blur(8px)',
        }}
      />
      <div style={{ position: 'relative', width: 480, height: 480 }}>
        <svg width={480} height={480} viewBox="0 0 480 480" style={{ transform: 'rotate(-90deg)' }}>
          <circle cx={240} cy={240} r={200} fill="none"
                  stroke="rgba(255,255,255,0.08)" strokeWidth={20} />
          <motion.circle
            cx={240} cy={240} r={200} fill="none"
            stroke={theme.ring} strokeWidth={20}
            strokeLinecap="round"
            strokeDasharray={C}
            initial={{ strokeDashoffset: C }}
            animate={{ strokeDashoffset: dashOffset }}
            transition={{ duration: 1.6, ease: [0.16, 1, 0.3, 1] }}
            style={{ filter: `drop-shadow(0 0 32px ${theme.glow})` }}
          />
        </svg>
        <div style={{
          position: 'absolute', inset: 0,
          display: 'flex', flexDirection: 'column',
          alignItems: 'center', justifyContent: 'center',
        }}>
          <div style={{ fontSize: 18, fontWeight: 700, letterSpacing: '0.32em',
                        textTransform: 'uppercase', color: 'rgba(255,255,255,0.55)',
                        marginBottom: 18 }}>
            🔥 Tonight's pulse
          </div>
          <div style={{
            fontSize: 180, fontWeight: 800, color: '#FFFFFF',
            lineHeight: 1, letterSpacing: '-0.04em',
            fontVariantNumeric: 'tabular-nums',
            textShadow: `0 0 60px ${theme.glow}`,
          }}>
            {score}
          </div>
          <div style={{
            fontSize: 38, fontWeight: 800, color: theme.ring,
            marginTop: 18, letterSpacing: '-0.02em',
            textShadow: `0 0 30px ${theme.glow}`,
          }}>
            {theme.label}
          </div>
        </div>
      </div>
    </div>
  );
};

const SceneBestSpin = ({ cat, audioOn }) => {
  const accent = CATEGORY_ACCENT[cat.category_slug] || CATEGORY_ACCENT.trending;
  return (
    <div style={sceneStage} data-testid={`venue-scene-${cat.category_slug}`}>
      {/* Background blur of the same thumbnail — cinematic depth */}
      {cat.thumb_url && (
        <div aria-hidden style={{
          position: 'absolute', inset: 0,
          background: `url(${cat.thumb_url}) center/cover no-repeat`,
          filter: 'blur(40px) saturate(1.6) brightness(0.7)',
          transform: 'scale(1.1)',
          opacity: 0.5,
        }} />
      )}
      <div aria-hidden style={{
        position: 'absolute', inset: 0,
        background: 'radial-gradient(circle at 50% 50%, transparent 0%, rgba(0,0,0,0.7) 80%)',
      }} />

      {/* Big category title above */}
      <motion.div
        initial={{ opacity: 0, y: 14 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7, delay: 0.1 }}
        style={{
          position: 'absolute', top: '8vh', left: 0, right: 0,
          textAlign: 'center', zIndex: 2,
        }}
      >
        <div style={{
          fontSize: 16, fontWeight: 700, letterSpacing: '0.32em',
          textTransform: 'uppercase', color: accent.color,
          textShadow: `0 0 24px ${accent.glow}`,
          marginBottom: 12,
        }}>
          {cat.category_icon} EventDrop Originals
        </div>
        <h1 style={{
          margin: 0, fontSize: 88, fontWeight: 800,
          color: '#FFFFFF', letterSpacing: '-0.025em',
          fontFamily: 'Georgia, "Times New Roman", serif',
          textShadow: `0 4px 32px rgba(0,0,0,0.5), 0 0 60px ${accent.glow}`,
        }}>
          {cat.category_label}
        </h1>
        <p style={{
          margin: '14px 0 0', fontSize: 22, fontWeight: 600,
          color: 'rgba(255,255,255,0.78)', letterSpacing: '0.04em',
        }}>
          {cat.metric_value} {cat.metric_label}
        </p>
      </motion.div>

      {/* Auto-playing vertical-cinematic video. `audioOn` is computed
          upstream by the rate-limiter — the parent decides whether
          this specific scene gets the sting (every ~25s max) or stays
          silent. We pass `muted` as a prop so React resets the audio
          state cleanly between scene rotations. */}
      <motion.div
        initial={{ opacity: 0, scale: 0.96 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.9, delay: 0.3, ease: [0.16, 1, 0.3, 1] }}
        style={{
          position: 'relative', zIndex: 2,
          height: '60vh', aspectRatio: '9 / 16',
          borderRadius: 28, overflow: 'hidden',
          border: `2px solid ${accent.color}`,
          boxShadow: `0 0 80px ${accent.glow}, 0 20px 60px rgba(0,0,0,0.65)`,
        }}
      >
        <video
          src={cat.video_url}
          autoPlay loop playsInline
          muted={!audioOn}
          data-testid={audioOn ? 'venue-video-audio-on' : 'venue-video-audio-off'}
          style={{
            width: '100%', height: '100%', objectFit: 'cover',
            display: 'block', background: '#000',
          }}
        />
      </motion.div>
    </div>
  );
};

const SceneLogoInterstitial = ({ eventName }) => (
  <div style={sceneStage} data-testid="venue-scene-logo">
    <motion.div
      initial={{ opacity: 0, letterSpacing: '0.5em', scale: 0.95 }}
      animate={{ opacity: 1, letterSpacing: '0.18em', scale: 1 }}
      transition={{ duration: 1.4, ease: [0.16, 1, 0.3, 1] }}
      style={{ textAlign: 'center' }}
    >
      <div style={{
        fontSize: 18, fontWeight: 700, letterSpacing: '0.4em',
        textTransform: 'uppercase', color: 'rgba(212,175,94,0.7)',
        marginBottom: 26,
      }}>
        ✦ Made with EventDrop
      </div>
      <h1 style={{
        margin: 0, fontSize: 112, fontWeight: 800,
        color: '#FFFFFF', letterSpacing: '-0.03em',
        fontFamily: 'Georgia, "Times New Roman", serif',
        textShadow: '0 0 80px rgba(184,151,98,0.5)',
      }}>
        {eventName || 'Tonight'}
      </h1>
      <div style={{
        marginTop: 38, fontSize: 16, fontWeight: 700,
        letterSpacing: '0.32em', textTransform: 'uppercase',
        color: 'rgba(255,255,255,0.5)',
      }}>
        Spin Film 360 · Live tonight
      </div>
    </motion.div>
  </div>
);

// Shared stage style — full-bleed, centred, no chrome.
const sceneStage = {
  position: 'absolute', inset: 0,
  display: 'flex', alignItems: 'center', justifyContent: 'center',
  background: 'radial-gradient(circle at 50% 50%, #14121f 0%, #050307 100%)',
  overflow: 'hidden',
};

// ────────────────────────────────────────────────────────────────────
//  VenueLivePage — top-level orchestrator.
// ────────────────────────────────────────────────────────────────────

const VenueLivePage = () => {
  const { slug } = useParams();
  const [eventDoc, setEventDoc] = useState(null);
  const [score, setScore] = useState(null);
  const [best, setBest] = useState([]);
  const [sceneIdx, setSceneIdx] = useState(0);
  const [pulseKey, setPulseKey] = useState(0);
  const [reasonFlash, setReasonFlash] = useState(null);
  // ── Audio unlock state ──
  // Browsers block autoplay-with-audio without a user gesture. The
  // tap-to-start overlay both grants that gesture AND requests
  // fullscreen + hides the cursor — the moment the projector becomes
  // the ambient environment instead of a webpage.
  // Embed mode (iter 170) — when the live page is rendered inside the
  // host's "Mirror what the venue sees ✦" preview, the audio gate and
  // the fullscreen request are skipped. The mirror is silent on
  // purpose: audio is venue-only, and asking the host to tap-to-start
  // inside an iframe would break the preview's "ambient confidence"
  // contract. Activated via `?embed=1` query string.
  const isEmbed = typeof window !== 'undefined'
    && new URLSearchParams(window.location.search).get('embed') === '1';
  const [audioUnlocked, setAudioUnlocked] = useState(isEmbed);
  // Rate-limit ref — last timestamp (ms) we played an audio scene.
  // Per the user brief, 1 audio moment every 20-40s. We pick 25s so
  // the silence between stings reinforces each one emotionally
  // instead of running them together.
  const lastAudioAtRef = useRef(0);
  // Sticky decision per sceneIdx so the boolean doesn't flap if React
  // re-renders mid-scene. Cleared on every scene change.
  const sceneAudioDecisionRef = useRef({ idx: -1, on: false });
  const esRef = useRef(null);

  const handleStart = () => {
    // Browsers require the unlock to happen inside the user-gesture
    // handler synchronously. Then we kick fullscreen async.
    setAudioUnlocked(true);
    try {
      const el = document.documentElement;
      const req = el.requestFullscreen || el.webkitRequestFullscreen;
      if (req) req.call(el).catch(() => {});
    } catch { /* ignore */ }
  };

  // Resolve event by slug
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await axios.get(`${API}/events/slug/${slug}`);
        if (!cancelled) setEventDoc(r.data);
      } catch {
        if (!cancelled) setEventDoc({ event_name: 'Event', event_id: null });
      }
    })();
    return () => { cancelled = true; };
  }, [slug]);

  const eventId = eventDoc?.event_id;

  // ── Memory Snap Hero Moment ──────────────────────────────────────
  // When a new Memory Snap arrives, the projector turns it into a
  // shared atmospheric beat — large polaroid materialises centre-stage,
  // tape lands, gold halo lingers ~3s, then fades. The hook drives a
  // stagger queue so multiple simultaneous arrivals play out as
  // distinct beats rather than chaotic overlap. Stays silent for
  // shared screens in noisy rooms.
  const { heroSnap: heroMemorySnap } = useMemorySnapArrivals(eventId, {
    intensity: 'projector',
    pollMs: 10_000,
    limit: 12,
    enabled: !!eventId,
  });

  // SSE subscription — drives the live pulse + score updates.
  useEffect(() => {
    if (!eventId) return undefined;
    let es;
    try {
      es = new EventSource(`${API}/events/${eventId}/spread-score/stream`);
      esRef.current = es;
      es.addEventListener('snapshot', (e) => {
        try {
          const snap = JSON.parse(e.data);
          setScore(snap);
          setPulseKey((k) => k + 1);
          // Translate reason codes into a human-readable flash.
          const r = snap._reason;
          if (r && !r.startsWith('keepalive')) {
            const map = {
              'spin_ready': '🎥 New spin landed',
              'playback': '👀 Someone watched',
              'share:open': '📱 Share opened',
              'share:share': '📱 Share opened',
              'share:copy_caption': '📋 Caption copied',
            };
            const flash = map[r] || (r.startsWith('share') ? '📱 Share opened' : null);
            if (flash) {
              setReasonFlash({ text: flash, key: Date.now() });
              setTimeout(() => setReasonFlash(null), 2800);
            }
          }
        } catch { /* ignore */ }
      });
      es.onerror = () => {
        // Auto-recovers — EventSource retries by default. We just
        // log silently to avoid spamming the projector.
      };
    } catch { /* noop */ }
    return () => { if (es) try { es.close(); } catch { /* ignore */ } };
  }, [eventId]);

  // Poll best-spins every 30s (separate from SSE — keeps the carousel fresh)
  useEffect(() => {
    if (!eventId) return undefined;
    let cancelled = false;
    const load = async () => {
      try {
        const r = await axios.get(`${API}/events/${eventId}/best-spins`);
        if (!cancelled) setBest(r.data?.categories || []);
      } catch { /* ignore */ }
    };
    load();
    const t = setInterval(load, 30000);
    return () => { cancelled = true; clearInterval(t); };
  }, [eventId]);

  // ── Compose the scene rotation list. Skip scenes with no data.
  // Iter 170 order: Score → Drop One Word ✦ → Memory Wall ticker →
  // Best Spins ×4 → Logo → QR plate every 5th cycle.
  const scenes = useMemo(() => {
    const out = [];
    if (score) out.push({ kind: 'score' });
    out.push({ kind: 'drop_one_word' });   // always — empty state renders calmly
    out.push({ kind: 'memory_wall' });     // always — empty state renders calmly
    best.forEach((cat) => out.push({ kind: 'best', cat }));
    out.push({ kind: 'logo' });
    return out;
  }, [score, best]);

  // Total scene-ticks since mount — drives the QR-plate cadence (every
  // 5 full rotations, the LAST scene is swapped for the QR plate so
  // latecomers see the doorway). Read-only computed from sceneIdx +
  // an internal tick counter.
  const globalTickRef = useRef(0);
  useEffect(() => {
    if (scenes.length === 0) return undefined;
    const t = setInterval(() => {
      globalTickRef.current += 1;
      setSceneIdx((i) => (i + 1) % scenes.length);
    }, SCENE_MS);
    return () => clearInterval(t);
  }, [scenes.length]);

  // QR scene injection: replace the LOGO slot every 5th rotation so
  // the rhythm of the show is preserved but the doorway is offered.
  const showQRThisCycle = useMemo(() => {
    if (scenes.length === 0) return false;
    const rotation = Math.floor(globalTickRef.current / scenes.length);
    const onLastOfRotation = (sceneIdx === scenes.length - 1);
    return onLastOfRotation && rotation > 0 && rotation % 5 === 0;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sceneIdx, scenes.length]);

  // Clamp sceneIdx to a valid index whenever scenes shrinks/grows
  const activeScene = scenes[sceneIdx % Math.max(1, scenes.length)] || scenes[0];

  // ── Rate-limited audio decision for the active scene ──
  // Sticky per-sceneIdx so the boolean doesn't flap if React
  // re-renders mid-scene. The decision is ONE shot per scene.
  const audioOnForScene = useMemo(() => {
    if (!audioUnlocked) return false;
    if (isEmbed) return false;  // Mirror preview is silent on purpose
    if (!activeScene || !SCENE_THAT_PLAYS_AUDIO.has(activeScene.kind)) return false;
    // Same scene we already decided on — return the cached decision
    // so it doesn't toggle while the same scene is on screen.
    if (sceneAudioDecisionRef.current.idx === sceneIdx) {
      return sceneAudioDecisionRef.current.on;
    }
    const now = Date.now();
    const elapsedSec = (now - lastAudioAtRef.current) / 1000;
    const on = elapsedSec >= VENUE_AUDIO_MIN_INTERVAL_SEC;
    sceneAudioDecisionRef.current = { idx: sceneIdx, on };
    if (on) lastAudioAtRef.current = now;
    return on;
  }, [audioUnlocked, activeScene, sceneIdx]);

  return (
    <div
      data-testid="venue-live-page"
      style={{
        position: 'fixed', inset: 0,
        background: '#050307',
        overflow: 'hidden',
        cursor: audioUnlocked ? 'none' : 'pointer',  // pointer until tap-to-start
      }}
    >
      {/* ── TAP-TO-START GATE ──────────────────────────────────────
          Browsers block autoplay-with-audio without a user gesture.
          We use that hard requirement as a UX feature — a deliberate
          "enter the experience" moment that doubles as the
          fullscreen request + cursor-hide trigger. After the tap the
          projector becomes the ambient environment instead of a
          webpage. ONLY rendered when audioUnlocked is false so it
          can't reappear mid-event. ── */}
      <AnimatePresence>
        {!audioUnlocked && (
          <motion.button
            key="audio-unlock"
            type="button"
            onClick={handleStart}
            data-testid="venue-audio-unlock"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
            style={{
              position: 'fixed', inset: 0, zIndex: 100,
              display: 'flex', flexDirection: 'column',
              alignItems: 'center', justifyContent: 'center',
              gap: 18, padding: 24, cursor: 'pointer',
              background: 'radial-gradient(circle at 50% 45%, rgba(20,18,32,0.96) 0%, #050307 75%)',
              color: '#FFFFFF', border: 'none',
              fontFamily: 'inherit',
            }}
          >
            <motion.div
              initial={{ opacity: 0, y: 18, scale: 0.94 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              transition={{ duration: 1.0, ease: [0.16, 1, 0.3, 1] }}
              style={{ textAlign: 'center' }}
            >
              <div style={{
                fontSize: 14, fontWeight: 700, letterSpacing: '0.4em',
                textTransform: 'uppercase', color: 'rgba(212,175,94,0.7)',
                marginBottom: 24,
              }}>
                ✦ EventDrop Originals
              </div>
              <h1 style={{
                margin: 0, fontSize: 64, fontWeight: 800,
                color: '#FFFFFF', letterSpacing: '-0.02em',
                fontFamily: 'Georgia, "Times New Roman", serif',
                maxWidth: '24ch', lineHeight: 1.1,
              }}>
                ✨ Tap to start the live experience
              </h1>
              <p style={{
                margin: '28px auto 0', maxWidth: 460,
                fontSize: 18, fontWeight: 500,
                color: 'rgba(255,255,255,0.62)', lineHeight: 1.5,
                letterSpacing: '0.02em',
              }}>
                Audio-enabled cinematic live wall.<br />
                Featured spins play with sound — the room becomes part of the moment.
              </p>
              {/* Subtle pulse hint */}
              <motion.div
                animate={{ scale: [1, 1.08, 1], opacity: [0.7, 1, 0.7] }}
                transition={{ duration: 2.4, ease: 'easeInOut', repeat: Infinity }}
                style={{
                  marginTop: 44, fontSize: 13, fontWeight: 700,
                  letterSpacing: '0.4em', textTransform: 'uppercase',
                  color: 'rgba(255,255,255,0.45)',
                }}
              >
                Tap anywhere
              </motion.div>
            </motion.div>
          </motion.button>
        )}
      </AnimatePresence>
      <AnimatePresence mode="wait">
        <motion.div
          key={`scene-${sceneIdx}-${showQRThisCycle ? 'qr' : 'normal'}`}
          initial={{ opacity: 0 }}
          animate={{ opacity: heroMemorySnap ? 0.55 : 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: heroMemorySnap ? 1.0 : 1.4, ease: [0.16, 1, 0.3, 1] }}
          style={{ position: 'absolute', inset: 0, filter: heroMemorySnap ? 'blur(2px)' : 'none', transition: 'filter 800ms ease-out' }}
        >
          {!activeScene && <SceneLogoInterstitial eventName={eventDoc?.event_name} />}
          {showQRThisCycle ? (
            <SceneQRPlate slug={slug} eventName={eventDoc?.event_name} />
          ) : (
            <>
              {activeScene?.kind === 'score' && score && (
                <SceneSpreadScore live={{ score }} pulseKey={pulseKey} />
              )}
              {activeScene?.kind === 'drop_one_word' && (
                <SceneDropOneWord slug={slug} />
              )}
              {activeScene?.kind === 'memory_wall' && (
                <SceneMemoryWallTicker eventId={eventId} eventName={eventDoc?.event_name} />
              )}
              {activeScene?.kind === 'best' && activeScene.cat && (
                <SceneBestSpin cat={activeScene.cat} audioOn={audioOnForScene} />
              )}
              {activeScene?.kind === 'logo' && (
                <SceneLogoInterstitial eventName={eventDoc?.event_name} />
              )}
            </>
          )}
        </motion.div>
      </AnimatePresence>

      {/* ── REASON FLASH — bottom-centred ribbon that fires on every
            SSE tick. Tells the room "something just happened" without
            interrupting the current scene. Per user brief: the ROOM
            should feel something just happened. ── */}
      <AnimatePresence>
        {reasonFlash && (
          <motion.div
            key={`flash-${reasonFlash.key}`}
            data-testid="venue-reason-flash"
            initial={{ opacity: 0, y: 14, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ type: 'spring', stiffness: 320, damping: 24 }}
            style={{
              position: 'fixed', bottom: 48, left: '50%',
              transform: 'translateX(-50%)', zIndex: 10,
              padding: '14px 28px', borderRadius: 999,
              background: 'rgba(20,18,32,0.85)',
              backdropFilter: 'blur(20px)',
              border: '1px solid rgba(212,175,94,0.5)',
              boxShadow: '0 0 32px rgba(212,175,94,0.35)',
              fontSize: 18, fontWeight: 700, color: '#FFFFFF',
              letterSpacing: '0.04em',
            }}
          >
            {reasonFlash.text}
          </motion.div>
        )}
      </AnimatePresence>

      {/* ── MEMORY SNAP HERO MOMENT ─────────────────────────────────
            When a new memory arrives the projector becomes the surface
            where it lands. The polaroid materialises centre-stage, the
            tape drops, and a gold halo lingers a few seconds before
            fading. Underlying scene rotation continues; the hero
            overlay sits above on z:90 (below the audio-unlock gate
            which is z:100). ── */}
      {heroMemorySnap && (
        <MemoryHeroMoment
          key={heroMemorySnap.snap_id}
          snap={heroMemorySnap}
          intensity="projector"
          anchor="center"
          testId={`venue-hero-${heroMemorySnap.snap_id}`}
        />
      )}

      {/* Permanent corner brand mark — tiny, gold, restrained */}
      <div style={{
        position: 'fixed', bottom: 22, right: 28, zIndex: 9,
        fontSize: 11, fontWeight: 700, letterSpacing: '0.32em',
        textTransform: 'uppercase', color: 'rgba(212,175,94,0.5)',
      }}>
        ✦ EventDrop
      </div>

      {/* Fullscreen [F] toggle — barely visible, auto-hides with
          the cursor. Operator escape hatch for tablet/laptop displays
          where ESC isn't always discoverable. Reuses the same cursor
          policy as the audio-unlock gate. */}
      {audioUnlocked && (
        <button
          data-testid="venue-fullscreen-toggle"
          onClick={() => {
            const el = document.documentElement;
            if (document.fullscreenElement || document.webkitFullscreenElement) {
              (document.exitFullscreen || document.webkitExitFullscreen)?.call(document);
            } else {
              (el.requestFullscreen || el.webkitRequestFullscreen)?.call(el).catch(() => {});
            }
          }}
          style={{
            position: 'fixed', top: 22, right: 28, zIndex: 9,
            width: 36, height: 36, borderRadius: 6,
            background: 'rgba(20,18,32,0.45)',
            border: '1px solid rgba(212,175,94,0.25)',
            color: 'rgba(212,175,94,0.55)',
            fontSize: 13, fontWeight: 700, letterSpacing: '0.06em',
            cursor: 'none',
            backdropFilter: 'blur(8px)',
            opacity: 0.4,
            transition: 'opacity 600ms ease-out',
          }}
          aria-label="Toggle fullscreen"
        >F</button>
      )}
    </div>
  );
};

export default VenueLivePage;
